/**
 * Enemy Manager - Spawns and manages enemy ships
 */

import { EnemyShip } from '../entities/EnemyShip.js';
import * as THREE from 'three';
import { clampLevel } from '../config/shipLevels.js';
import { GAME_BOUNDARY_SIZE } from '../utils/constants.js';

export class EnemyManager {
    constructor(game) {
        this.game = game;
        this.enemies = new Map();
        this.isActive = false;

        // Base difficulty settings (基础难度参数)
        this.baseDifficulty = {
            minEnemies: 12,
            maxEnemies: 15,
            targetEnemyCount: 14,
            spawnRateMs: 320,
            maxAggressiveChasers: 4
        };

        // High difficulty settings (高难度参数，4-5个护航船时启用)
        this.highDifficulty = {
            minEnemies: 16,
            maxEnemies: 22,
            targetEnemyCount: 19,
            spawnRateMs: 220,
            maxAggressiveChasers: 7
        };

        // Current difficulty settings (当前难度参数)
        this.minEnemies = this.baseDifficulty.minEnemies;
        this.maxEnemies = this.baseDifficulty.maxEnemies;
        this.targetEnemyCount = this.baseDifficulty.targetEnemyCount;
        this.spawnRateMs = this.baseDifficulty.spawnRateMs;
        this.maxAggressiveChasers = this.baseDifficulty.maxAggressiveChasers;

        this.spawnRadius = 60; // 生成半径
        this.spawnPoints = this.generateSpawnPoints();

        // Spawn smoothing
        this.pendingSpawns = [];
        this.maxSpawnsPerFrame = 1;
        this.lastActualSpawn = 0;

        // Deferred removal to avoid同帧大量销毁阻塞
        this.pendingRemovals = [];
        this.maxRemovalsPerFrame = 2;

        this._aggFrameId = -1;
        this._activeAggressorsPrev = new Set();
        this._activeAggressorsNext = new Set();

        // Difficulty tracking
        this.currentDifficultyLevel = 'base'; // 'base' 或 'high'
    }

    requestAggressorSlot(enemy) {
        if (!enemy || enemy.aiPersonality !== 'aggressive') {
            return false;
        }

        const frameId = this.game?.frameId ?? 0;
        if (frameId !== this._aggFrameId) {
            this._aggFrameId = frameId;
            this._activeAggressorsPrev = this._activeAggressorsNext;
            this._activeAggressorsNext = new Set();
        }

        const id = enemy.id;
        if (!id) return false;

        if (this._activeAggressorsNext.has(id)) {
            return true;
        }

        if (this._activeAggressorsPrev.has(id)) {
            this._activeAggressorsNext.add(id);
            return true;
        }

        if (this._activeAggressorsNext.size >= this.maxAggressiveChasers) {
            return false;
        }

        this._activeAggressorsNext.add(id);
        return true;
    }

    start() {
        this.isActive = true;

        // Spawn initial enemies
        this.spawnInitialEnemies();
    }

    stop() {
        this.isActive = false;

        // Remove all enemies
        this.clearAllEnemies();
    }

    /**
     * 根据玩家护航船数量动态调整难度
     * 当玩家有4个或以上护航船时，提升难度
     */
    updateDifficulty() {
        if (!this.game.rescueManager) return;

        const followerCount = this.game.rescueManager.followerSlotsUsed || 0;
        const shouldUseHighDifficulty = followerCount >= 4;

        // 检查是否需要切换难度
        if (shouldUseHighDifficulty && this.currentDifficultyLevel === 'base') {
            // 切换到高难度
            this.currentDifficultyLevel = 'high';
            this.minEnemies = this.highDifficulty.minEnemies;
            this.maxEnemies = this.highDifficulty.maxEnemies;
            this.targetEnemyCount = this.highDifficulty.targetEnemyCount;
            this.spawnRateMs = this.highDifficulty.spawnRateMs;
            this.maxAggressiveChasers = this.highDifficulty.maxAggressiveChasers;

            console.log(`🔥 难度提升！护航船数量: ${followerCount}, 新难度参数:`, {
                maxEnemies: this.maxEnemies,
                spawnRateMs: this.spawnRateMs,
                maxAggressiveChasers: this.maxAggressiveChasers
            });
        } else if (!shouldUseHighDifficulty && this.currentDifficultyLevel === 'high') {
            // 切换回基础难度
            this.currentDifficultyLevel = 'base';
            this.minEnemies = this.baseDifficulty.minEnemies;
            this.maxEnemies = this.baseDifficulty.maxEnemies;
            this.targetEnemyCount = this.baseDifficulty.targetEnemyCount;
            this.spawnRateMs = this.baseDifficulty.spawnRateMs;
            this.maxAggressiveChasers = this.baseDifficulty.maxAggressiveChasers;

            console.log(`📉 难度降低！护航船数量: ${followerCount}, 恢复基础难度`);
        }
    }

    update(deltaTime) {
        if (!this.isActive) return;

        // 动态调整难度（根据玩家护航船数量）
        this.updateDifficulty();

        // Update all enemies and check for removal
        const enemiesToRemove = [];
        this.enemies.forEach((enemy, id) => {
            enemy.update(deltaTime);

            // Check if enemy should be removed (destroyed or marked for removal)
            if (enemy.shouldRemove || enemy.health <= 0) {
                enemiesToRemove.push(id);
            }
        });

        // Schedule removals with throttling
        enemiesToRemove.forEach(id => {
            const enemy = this.enemies.get(id);
            if (!enemy || enemy._pendingRemoval) return;

            const killer = enemy.killerRef || null;
            const killerFaction = killer && killer.faction ? killer.faction : (enemy.killerFaction || null);
            const isPlayerKill = killerFaction === 'player';

            // 调试日志
            console.log(`Enemy ${enemy.name} destroyed:`, {
                killer: killer ? (killer.name || killer.id || 'unknown') : 'null',
                killerFaction,
                isPlayerKill,
                enemy_killerRef: enemy.killerRef ? 'exists' : 'null',
                enemy_killerFaction: enemy.killerFaction,
                enemy_lastAttacker: enemy.lastAttacker ? 'exists' : 'null'
            });

            if (isPlayerKill) {
                if (this.game.rankingSystem) {
                    this.game.rankingSystem.addEnemyKill();
                    console.log(`✓ Player kill counted. Total kills: ${this.game.rankingSystem.enemiesDestroyed}`);
                }
                if (this.game.player) {
                    this.game.player.addScore(100); // 玩家阵营击杀奖励分数
                }
                if (this.game.rankingUI) {
                    this.game.rankingUI.showKillNotification(enemy.name);
                }
            } else if (killerFaction === 'enemy' && killer) {
                killer.enemyKills = (killer.enemyKills || 0) + 1;
                console.log(`Enemy-on-enemy kill, not counted for player`);
            } else {
                console.log(`Kill faction: ${killerFaction}, not counted as player kill`);
            }

            // Save enemy gold to persistent slot before removal
            if (this.game.rankingSystem && enemy.baseName) {
                const currentGold = enemy.collectedCoins || 0;
                this.game.rankingSystem.saveEnemyGold(enemy.baseName, currentGold);
            }

            enemy.shouldRemove = true;
            enemy._pendingRemoval = true;
            this.enemies.delete(id);
            this.pendingRemovals.push({ id });
        });

        this.processRemovalQueue();

        // Spawn new enemies if needed
        this.checkSpawning();
        this.processSpawnQueue();
    }

    spawnInitialEnemies() {
        const desired = this.getDesiredLevelCounts();
        this.targetEnemyCount = Array.from(desired.values()).reduce((acc, val) => acc + val, 0);
        let delay = 0;
        desired.forEach((count, level) => {
            for (let i = 0; i < count; i++) {
                this.queueSpawn(level, { readyAt: Date.now() + delay });
                delay += 220;
            }
        });
    }

    checkSpawning() {
        const now = Date.now();
        const desired = this.getDesiredLevelCounts();
        const desiredTotal = Array.from(desired.values()).reduce((acc, val) => acc + val, 0);
        this.targetEnemyCount = desiredTotal;
        const effectiveCount = this.enemies.size + this.pendingSpawns.length + this.pendingRemovals.length;

        if (effectiveCount >= desiredTotal) {
            return;
        }

        const deficit = desiredTotal - effectiveCount;
        for (let i = 0; i < deficit; i++) {
            this.queueSpawn(null, { readyAt: now + i * 180 + 60 });
        }
    }

    spawnEnemy(levelOverride = null) {
        if (this.enemies.size >= this.maxEnemies) {
            return;
        }
        // Generate spawn position using discrete spawn points
        const spawnPos = this.getRandomSpawnPosition();
        const enemyId = `enemy_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

        // Get random name from ranking system
        const enemyName = this.game.rankingSystem ?
            this.game.rankingSystem.getRandomEnemyName() :
            `Enemy ${Math.floor(Math.random() * 1000)}`;

        const enemyLevel = levelOverride ?? this.chooseLevelForSpawn();

        // 随机分配AI性格：30%攻击性，70%巡逻型
        const aiPersonality = Math.random() < 0.3 ? 'aggressive' : 'passive';

        const enemy = new EnemyShip({
            game: this.game,
            position: spawnPos,
            id: enemyId,
            name: enemyName,
            level: enemyLevel,
            aiPersonality: aiPersonality // 设置AI性格
        });

        // 异步初始化，不阻塞主线程
        enemy.init().then(() => {
            // 只有在初始化成功后才添加到游戏中
            if (!enemy.shouldRemove) {
                // Add to enemies map
                this.enemies.set(enemyId, enemy);
                this.game.addGameObject(enemyId, enemy);

                // Restore enemy gold from persistent slot
                if (this.game.rankingSystem && enemy.baseName) {
                    const savedGold = this.game.rankingSystem.getEnemyGold(enemy.baseName);
                    enemy.collectedCoins = savedGold;
                    console.log(`Enemy ${enemy.baseName} spawned at level ${enemy.level} with ${savedGold} gold (name: ${enemy.name})`);
                } else {
                    console.log(`Enemy ${enemy.name} spawned at level ${enemy.level}`);
                }

                // 根据敌船与玩家等级差距自动给副船
                const playerLevel = this.game?.player?.level || 1;
                const escortCount = this.getEscortCountByLevelDiff(enemyLevel, playerLevel);
                for (let i = 0; i < escortCount; i++) {
                    setTimeout(() => {
                        // 确保敌船仍然存在且物理体准备好
                        if (enemy.escorts && enemy.body && enemy.body.position &&
                            enemy.escorts.length < enemy.maxEscorts && !enemy.shouldRemove) {
                            enemy.addEscort().catch(err => {
                                console.warn('Failed to add initial escort:', err);
                            });
                        }
                    }, i * 300); // 增加到300ms间隔，给敌船更多初始化时间
                }
            }
        }).catch(error => {
            console.warn('Failed to spawn enemy:', error);
            // 清理失败的敌船
            if (enemy.dispose) enemy.dispose();
        });

        this.lastActualSpawn = Date.now();
    }

    getRandomSpawnPosition() {
        const player = this.game.getGameObject('player');
        const playerPos = player?.body?.position || new THREE.Vector3(0, 0.1, 0);

        let bestPoint = null;
        let bestScore = -Infinity;

        const occupiedDistance = 18;
        const playerBuffer = 35;

        this.spawnPoints.forEach((point) => {
            const candidate = point.clone();

            const distanceToPlayer = candidate.distanceTo(playerPos);
            if (distanceToPlayer < playerBuffer) {
                return; // 太靠近玩家
            }

            let nearestEnemyDistance = Infinity;
            this.enemies.forEach((enemy) => {
                if (!enemy || !enemy.body || enemy.health <= 0) return;
                const enemyPos = enemy.body.position;
                const distance = candidate.distanceTo(enemyPos);
                if (distance < nearestEnemyDistance) {
                    nearestEnemyDistance = distance;
                }
            });

            if (nearestEnemyDistance < occupiedDistance) {
                return; // 刷新点被邻近敌船占用
            }

            const score = distanceToPlayer + nearestEnemyDistance * 0.6 + Math.random();
            if (score > bestScore) {
                bestScore = score;
                bestPoint = candidate;
            }
        });

        if (bestPoint) {
            return bestPoint;
        }

        return this.getFallbackSpawnPosition(playerPos);
    }

    getFallbackSpawnPosition(playerPos) {
        let spawnPos = new THREE.Vector3(0, 0.1, 0);
        const maxAttempts = 12;
        let attempts = 0;

        do {
            const angle = Math.random() * Math.PI * 2;
            const distance = this.spawnRadius + Math.random() * 30;
            spawnPos.set(
                Math.cos(angle) * distance,
                0.1,
                Math.sin(angle) * distance
            );
            attempts++;
        } while (attempts < maxAttempts && spawnPos.distanceTo(playerPos) < 30);

        const boundaryLimit = GAME_BOUNDARY_SIZE - 5;
        spawnPos.x = Math.max(-boundaryLimit, Math.min(boundaryLimit, spawnPos.x));
        spawnPos.z = Math.max(-boundaryLimit, Math.min(boundaryLimit, spawnPos.z));

        return spawnPos.clone();
    }

    queueSpawn(levelOverride = null, options = {}) {
        if (this.pendingSpawns.length >= this.maxEnemies * 2) {
            return;
        }

        const entry = {
            levelOverride,
            readyAt: options.readyAt ?? Date.now(),
            attempts: 0
        };

        this.pendingSpawns.push(entry);
    }

    processSpawnQueue() {
        if (!this.pendingSpawns.length) return;

        const now = Date.now();
        if (now - this.lastActualSpawn < this.spawnRateMs) {
            return;
        }

        let processed = 0;
        for (let i = 0; i < this.pendingSpawns.length && processed < this.maxSpawnsPerFrame; i++) {
            const entry = this.pendingSpawns[i];
            if (!entry || entry.readyAt > now) {
                continue;
            }

            this.pendingSpawns.splice(i, 1);
            i--;

            if (this.enemies.size >= this.maxEnemies) {
                continue;
            }

            this.spawnEnemy(entry.levelOverride ?? undefined);
            processed++;
        }
    }

    processRemovalQueue() {
        if (!this.pendingRemovals.length) return;

        let processed = 0;
        while (processed < this.maxRemovalsPerFrame && this.pendingRemovals.length) {
            const entry = this.pendingRemovals.shift();
            if (entry && entry.id) {
                this.game.removeGameObject(entry.id);
            }
            processed++;
        }
    }

    generateSpawnPoints() {
        const points = [];
        const maxRadius = GAME_BOUNDARY_SIZE - 15;
        const rings = [this.spawnRadius, this.spawnRadius + 25, this.spawnRadius + 45]
            .map(radius => Math.min(radius, maxRadius));
        const segmentsPerRing = 12;

        rings.forEach((radius, ringIndex) => {
            for (let i = 0; i < segmentsPerRing; i++) {
                const angle = (i / segmentsPerRing) * Math.PI * 2 + (ringIndex * Math.PI / segmentsPerRing);
                const x = Math.cos(angle) * radius;
                const z = Math.sin(angle) * radius;
                points.push(new THREE.Vector3(x, 0.1, z));
            }
        });

        return points;
    }

    removeEnemy(enemyId) {
        if (this.enemies.has(enemyId)) {
            this.enemies.delete(enemyId);
        }
    }

    clearAllEnemies() {
        this.enemies.forEach((enemy, id) => {
            enemy.shouldRemove = true;
            enemy._pendingRemoval = true;
            this.game.removeGameObject(id);
        });
        this.enemies.clear();
        this.pendingSpawns = [];
        this.pendingRemovals = [];
    }

    clearEnemies() {
        this.clearAllEnemies();
    }

    getEnemyCount() {
        return this.enemies.size;
    }

    // Get enemy at position for collision detection
    getEnemyAt(position, radius = 5) {
        for (let [id, enemy] of this.enemies) {
            if (enemy.body && enemy.body.position.distanceTo(position) < radius) {
                return enemy;
            }
        }
        return null;
    }

    getDesiredLevelCounts() {
        const playerLevel = clampLevel(this.game?.player?.level || 1);
        const tiers = [
            { offset: 1, count: 4 },
            { offset: 0, count: 5 },
            { offset: -1, count: 4 },
            { offset: -2, count: 3 },
            { offset: -3, count: 2 }
        ];

        const desired = new Map();
        tiers.forEach(({ offset, count }) => {
            const level = clampLevel(playerLevel + offset);
            desired.set(level, (desired.get(level) || 0) + count);
        });

        const desiredTotal = Array.from(desired.values()).reduce((acc, val) => acc + val, 0);
        if (desiredTotal > this.maxEnemies) {
            let extra = desiredTotal - this.maxEnemies;
            const levelOrder = Array.from(desired.keys());
            while (extra > 0) {
                for (let i = 0; i < levelOrder.length && extra > 0; i++) {
                    const level = levelOrder[i];
                    const current = desired.get(level);
                    if (current > 0) {
                        desired.set(level, current - 1);
                        extra--;
                    }
                }
            }
        }

        const clampedTotal = Array.from(desired.values()).reduce((acc, val) => acc + val, 0);
        if (clampedTotal < this.minEnemies) {
            const deficit = this.minEnemies - clampedTotal;
            const levelOrder = Array.from(desired.keys());
            for (let i = 0; i < deficit; i++) {
                const level = levelOrder[i % levelOrder.length];
                desired.set(level, (desired.get(level) || 0) + 1);
            }
        }
        return desired;
    }

    getCurrentLevelCounts() {
        const counts = new Map();
        this.enemies.forEach((enemy) => {
            const level = clampLevel(enemy.level || 1);
            counts.set(level, (counts.get(level) || 0) + 1);
        });
        return counts;
    }

    chooseLevelForSpawn() {
        const desired = this.getDesiredLevelCounts();
        const counts = this.getCurrentLevelCounts();
        let chosenLevel = clampLevel(this.game?.player?.level || 1);
        let maxDeficit = -Infinity;

        desired.forEach((target, level) => {
            const deficit = target - (counts.get(level) || 0);
            if (deficit > maxDeficit) {
                maxDeficit = deficit;
                chosenLevel = level;
            }
        });

        if (maxDeficit <= 0) {
            chosenLevel = clampLevel(this.game?.player?.level || 1);
        }

        this.targetEnemyCount = Array.from(desired.values()).reduce((acc, val) => acc + val, 0);
        return chosenLevel;
    }

    // 根据敌船与玩家等级差距返回副船数量（提高副船数量增加挑战性）
    // levelDiff = enemyLevel - playerLevel
    // 低于主船2级或更多: 2-4个副船
    // 低于主船1级: 0-4个副船
    // 同级或高于主船: 0-2个副船
    getEscortCountByLevelDiff(enemyLevel, playerLevel) {
        const levelDiff = enemyLevel - playerLevel;

        if (levelDiff <= -2) {
            // 低于主船2级或更多: 2-4个副船（随机）
            return 2 + Math.floor(Math.random() * 3); // 2, 3, 或 4
        } else if (levelDiff === -1) {
            // 低于主船1级: 0-4个副船（随机）
            return Math.floor(Math.random() * 5); // 0, 1, 2, 3, 或 4
        } else {
            // 同级或高于主船1级: 0-2个副船（随机）
            return Math.floor(Math.random() * 3); // 0, 1, 或 2
        }
    }
}
